import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Check, MoreHorizontal, X } from 'lucide-react';
import type { ApprovalStatus } from './api';
import { architectureReviewView } from './architectureReview';
import { fmtDate } from './utils';

import { LatoAiLauncher } from './LatoAi';
import type { WorkspaceStage } from './WorkspaceStageNav';
import './stage-chrome.css';

function approvalTooltip(status: ApprovalStatus) {
  return status.approvals.map(vote => `${vote.user_name || 'Approved'}${vote.approved_at ? ' · ' + fmtDate(vote.approved_at) : ''}${vote.actor_name && vote.actor_user_id !== vote.user_id ? ' · by ' + vote.actor_name + ' (Root)' : ''}`).join('\n') || 'No approvals yet';
}


export interface StageAction {
  id: string; label: string; icon?: React.ReactNode; onClick: () => void;
  disabled?: boolean;
}

/** Same quiet toolbar on every stage. Document tools belong to the menu. */
export function StageHeader({ title, status, actions = [], extraActions, leading, decision, children, className = '' }: {
  title: string; status?: React.ReactNode; actions?: StageAction[];
  leading?: React.ReactNode; extraActions?: React.ReactNode; decision?: React.ReactNode; children?: React.ReactNode; className?: string;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null); const trigger = useRef<HTMLButtonElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    root.current?.querySelector<HTMLButtonElement>('[role=menuitem]:not(:disabled)')?.focus();
    const outside = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(false); };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') { event.preventDefault(); setOpen(false); trigger.current?.focus(); } };
    document.addEventListener('pointerdown', outside); document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape); };
  }, [open]);
  return <header className={'stage-header' + (decision ? ' has-decision' : '') + ' ' + className}>
    {leading ? <div className="stage-header-leading">{leading}</div> : null}
    <h2>{title}</h2>
    {status && !(decision && status === 'Approved') ? <span className="stage-header-status" role="status">{status}</span> : null}
    {children}
    {decision}
    <div ref={root} className="stage-menu">
      <button ref={trigger} type="button" className="stage-icon-button" aria-label={`${title} actions`}
        aria-haspopup="menu" aria-expanded={open} aria-controls={id} onKeyDown={event => { if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); setOpen(true); } }} onClick={() => setOpen(value => !value)}>
        <MoreHorizontal size={19} strokeWidth={1.8}/>
      </button>
      {open ? <div id={id} className="stage-menu-popover" role="menu" aria-label={`${title} actions`} onKeyDown={event => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const buttons = Array.from(root.current?.querySelectorAll<HTMLButtonElement>('[role=menuitem]:not(:disabled)') || []);
        if (!buttons.length) return;
        const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (current + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
        buttons[next].focus();
      }}>
        <div className="stage-menu-heading"><span>{title}</span><button type="button" className="stage-icon-button" aria-label="Close actions" onClick={() => { setOpen(false); trigger.current?.focus(); }}><X size={16}/></button></div>
        {actions.length ? actions.map(action => <button key={action.id} type="button" role="menuitem" disabled={action.disabled}
          onClick={() => { setOpen(false); action.onClick(); }}>{action.icon ? <span aria-hidden="true">{action.icon}</span> : null}<span>{action.label}</span></button>) : <span className="stage-menu-empty">No additional actions</span>}
        {extraActions}
      </div> : null}
    </div>
  </header>;
}

const LABEL = { architecture: 'Architecture', estimate: 'Estimate', pricing: 'Pricing', proposal: 'Proposal' };
const NUMBER = { architecture: '01', estimate: '02', pricing: '03', proposal: '04' };

/** Approved legacy documents retain their approval even without vote rows. */
export function stageApprovalCount(status: ApprovalStatus | null | undefined, pending: boolean, count?: { given: number; required: number }) {
  const required = Math.max(1, count?.required ?? status?.required ?? 1);
  const recorded = Math.max(0, count?.given ?? status?.given ?? 0);
  return { required, given: pending ? Math.min(recorded, required) : required };
}

export function StageFooter({ stage, title, status, pending, userId, count, hint, children, className = '', placement = 'footer' }: {
  stage: WorkspaceStage; title?: string; status?: ApprovalStatus | null; pending: boolean;
  userId?: string | null; count?: { given: number; required: number }; hint?: React.ReactNode;
  children?: React.ReactNode; className?: string; placement?: 'footer' | 'header';
}) {
  const progress = stageApprovalCount(status, pending, count);
  const mine = pending && status?.me.has_approved && status.approvals.some(vote => vote.user_id === userId);
  const waiting = pending && status && !status.me.can_approve;
  const review = pending ? architectureReviewView(status, stage) : null;
  const detail = hint || review?.hint || (mine ? 'Your approval is recorded' : waiting
    ? status.root_only ? 'Waiting for Root' : status.turn ? `Waiting for ${status.turn.name}` : `Waiting for ${(status.approvers || []).map(user => user.display_name || user.username).filter(Boolean).join(', ') || 'assigned approvers'}` : null);
  const visibleActions = React.Children.toArray(children);
  if (placement === 'header') return <div className={'stage-decision-header' + (!pending ? ' is-approved' : '') + ' ' + className} role="group" aria-label={`${LABEL[stage]} review`}>
    <div className="stage-decision-summary">
      <span className={'stage-approval-count' + (!pending ? ' is-complete' : '')} title={status ? approvalTooltip(status) : title}>
        {!pending ? <Check size={13} strokeWidth={2}/> : null}Approved {progress.given} of {progress.required}
      </span>
      {detail ? <small className="stage-decision-hint">{detail}</small> : null}
    </div>
    {visibleActions.length ? <div className="stage-decision-actions">{visibleActions}</div> : null}
  </div>;
  return <footer className={'arch-stage-decision stage-footer' + (!pending ? ' is-approved' : '') + ' ' + className}>
    <div className="stage-footer-copy">
      <span className="stage-footer-kicker">{NUMBER[stage]} / {LABEL[stage]}</span>
      <div className="stage-footer-line"><strong>{title || review?.title || (!pending ? `${LABEL[stage]} approved` : 'Ready for review')}</strong>
        <span className={'stage-approval-count' + (!pending ? ' is-complete' : '')} title={status ? approvalTooltip(status) : undefined}>
          {!pending ? <Check size={13} strokeWidth={2}/> : null}Approved {progress.given} of {progress.required}
        </span>
      </div>
      {detail ? <small className="stage-footer-hint">{detail}</small> : null}
    </div>
    {visibleActions.length ? <div className="stage-footer-actions">{visibleActions}</div> : null}
  </footer>;
}

/** A zero-height dock keeps the same side position without shrinking content. */
export function StageAiButton({ open = false, onClick, disabled, busy, mini = false }: {
  open?: boolean; onClick: () => void; disabled?: boolean; busy?: boolean; mini?: boolean;
}) {
  const dock = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState<number | undefined>();
  useLayoutEffect(() => {
    if (!mini) return;
    const footer = dock.current?.nextElementSibling;
    if (!(footer instanceof HTMLElement) || !footer.classList.contains('stage-footer')) return;
    const measure = () => setFooterHeight(footer.getBoundingClientRect().height);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure); observer.observe(footer);
    return () => observer.disconnect();
  }, [mini]);
  return <div ref={dock} style={mini && footerHeight ? { bottom: footerHeight } : undefined} className={'stage-ai-dock' + (mini ? ' is-mini' : '')}><LatoAiLauncher open={open} onClick={onClick} disabled={disabled} busy={busy}/></div>;
}
